import React from 'react';
import { Leaf, Shield, Award, Globe } from 'lucide-react';
import { features } from '../data/mock';

const iconMap = {
  Leaf: Leaf,
  Shield: Shield,
  Award: Award,
  Globe: Globe
};

const About = () => {
  return (
    <section id="about" className="py-20 bg-gradient-to-b from-white to-amber-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-orange-500 font-semibold text-sm uppercase tracking-wider">About Us</span>
          <h2 className="text-4xl md:text-5xl font-bold text-amber-950 mt-3 mb-4">
            Why Choose Cornerstone Exim
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            We are committed to delivering the finest agricultural products from India to the world, 
            ensuring quality, sustainability, and reliability at every step.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => {
            const Icon = iconMap[feature.icon];
            return (
              <div 
                key={index}
                className="group bg-white rounded-2xl p-8 shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-green-200"
              >
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-green-700 transition-colors duration-300">
                  <Icon className="text-green-700 group-hover:text-white transition-colors duration-300" size={28} />
                </div>
                <h3 className="text-xl font-bold text-amber-950 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            );
          })}
        </div>

        {/* Founder & Mission Section */}
        <div className="mt-20 grid lg:grid-cols-2 gap-8">
          {/* Founder Section */}
          <div className="bg-white rounded-3xl p-10 shadow-lg border border-gray-100">
            <h3 className="text-3xl font-bold text-amber-950 mb-4">Founded with Experience</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              Cornerstone Exim was founded in 2025 by <span className="font-bold text-green-700">Komal Chikane</span>, 
              bringing extensive expertise from leading Indian startups and logistics companies.
            </p>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-green-700 rounded-full mt-2"></div>
                <div>
                  <p className="font-semibold text-amber-950">Category Manager at Zepto</p>
                  <p className="text-sm text-gray-600">Fresh produce & supply chain management</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-green-700 rounded-full mt-2"></div>
                <div>
                  <p className="font-semibold text-amber-950">Key Account Manager at Dunzo</p>
                  <p className="text-sm text-gray-600">B2B partnerships & operations</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-green-700 rounded-full mt-2"></div>
                <div>
                  <p className="font-semibold text-amber-950">Experience at Bounce</p>
                  <p className="text-sm text-gray-600">Logistics & operational excellence</p>
                </div>
              </div>
            </div>
          </div>

          {/* Mission Statement */}
          <div className="bg-gradient-to-r from-green-700 to-green-800 rounded-3xl p-10 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full -ml-48 -mb-48"></div>
            <div className="relative z-10">
              <h3 className="text-3xl font-bold mb-6">Our Mission</h3>
              <p className="text-lg text-green-50 leading-relaxed">
                At Cornerstone Exim, we bridge the gap between Indian farmers and international markets. 
                By procuring directly from farms, we ensure fair prices for farmers while delivering 
                premium quality agricultural products to our global clients. Our commitment to sustainability, 
                quality, and transparency drives everything we do.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
